import { useState, useEffect, useRef } from 'react';
import { doc, getDoc, runTransaction, Timestamp, onSnapshot } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { v4 as uuidv4 } from 'uuid';
import type { Participant, ParticipantStatus, ParticipantMetadata } from '../types/participant';
import Analytics from '../services/analytics';

export function useParticipant(decisionId: string) {
  const LOCAL_STORAGE_KEY = 'whatSayUs_participant';
  const loadingRef = useRef(false);
  const mountedRef = useRef(true);
  const [participant, setParticipant] = useState<Participant | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [requiresAuth, setRequiresAuth] = useState(false);
  const [anonymousDisabled, setAnonymousDisabled] = useState(false);

  // In useParticipant.ts
  useEffect(() => { 
    mountedRef.current = true;
    let participantUnsubscribe: (() => void) | null = null;

    const loadOrCreateParticipant = async () => {
      if (!mountedRef.current || !decisionId) {
        return;
      }
      
      if (loadingRef.current) {
        console.debug("Skipping load - already loading");
        return;
      }
    
      loadingRef.current = true;
    
      try {
        // First get decision data to check requirements and limits
        const decisionRef = doc(db, 'decisions', decisionId);
        const decisionSnap = await getDoc(decisionRef);
        
        if (!decisionSnap.exists()) {
          throw new Error('Decision not found');
        }
      
        const decisionData = decisionSnap.data();
        const currentParticipants = decisionData.participants || 0;
        
        if (!auth.currentUser && currentParticipants >= 20) {  
          setAnonymousDisabled(true);
          setParticipant(null);
          if (mountedRef.current) {
            setRequiresAuth(!!decisionData.requiresAuth);
            setIsLoading(false);
          }
          return;
        }

        let participantId: string | null = null;

        if (auth.currentUser) {
          participantId = auth.currentUser.uid;
        } else {
          const storedData = localStorage.getItem(LOCAL_STORAGE_KEY);
          if (storedData) {
            const metadata: ParticipantMetadata = JSON.parse(storedData);
            if (metadata.pastDecisions.includes(decisionId)) {
              participantId = metadata.localId;
            }
          }
        }

        if (participantId) {
          // Set up participant subscription only if we don't already have one
          if (!participantUnsubscribe) {
            participantUnsubscribe = onSnapshot(
              doc(db, 'decisions', decisionId, 'participants', participantId),
              (participantSnap) => {
                if (participantSnap.exists()) {
                  setParticipant(participantSnap.data() as Participant);
                } else {
                  setParticipant(null);
                }
                if (mountedRef.current) {
                  setIsLoading(false);
                }
              },
              (error) => {
                console.error('Participant subscription error:', error);
                if (mountedRef.current) {
                  setError('Failed to load participant data');
                  setIsLoading(false);
                }
              }
            );
          }
        } else {
          setParticipant(null);
          if (mountedRef.current) {
            setIsLoading(false);
          }
        }
    
        if (mountedRef.current) {
          setRequiresAuth(!!decisionData.requiresAuth);
        }
    
      } catch (err) {
        if (mountedRef.current) {
          console.error('Error in loadOrCreateParticipant:', err);
          setError('Failed to load participant data');
          setIsLoading(false);
        }
      } finally {
        loadingRef.current = false;
      }
    };

    loadOrCreateParticipant();

    return () => {
      mountedRef.current = false;
      loadingRef.current = false;
      if (participantUnsubscribe) {
        participantUnsubscribe();
      }
    };
  }, [decisionId]);

  const formatDisplayName = (name: string, authType: 'anonymous' | 'google'): string => {
    if (authType !== 'google' || !name) return name;
    
    const nameParts = name.trim().split(/\s+/);
    if (nameParts.length === 1) return nameParts[0];
    
    const firstName = nameParts[0];
    const lastInitial = nameParts[nameParts.length - 1].charAt(0);
    
    return `${firstName} ${lastInitial}.`;
};

  const joinDecision = async (name?: string) => {

    const MAX_JOINS_PER_HOUR = 10;
    const joinKey = `joinCount_${Date.now() - (Date.now() % 3600000)}`;
    const joins = parseInt(localStorage.getItem(joinKey) || '0', 10);

    // Increment join counter after successful join
    localStorage.setItem(joinKey, (joins + 1).toString());

    if (joins >= MAX_JOINS_PER_HOUR) {
      throw new Error('Too many join attempts. Please try again later.');
    }

    try {
      const now = Timestamp.now();
  
      const decisionRef = doc(db, 'decisions', decisionId);
      const decisionSnap = await getDoc(decisionRef);
      
      if (!decisionSnap.exists()) {
        throw new Error('Decision not found');
      }
  
      const decisionData = decisionSnap.data();
      const currentParticipants = decisionData.participants || 0;
  
      // If user is not authenticated and we're at limit, suggest sign in
      if (!auth.currentUser && currentParticipants >= 20) {
        throw new Error(
          'This discussion has reached the maximum number of anonymous participants. ' +
          'Please sign in with Google to join.'
        );
      }
  
      let participantId: string;
      let authType: 'anonymous' | 'google' = 'anonymous';
      let email: string | undefined;
      let displayName: string;
  
      if (auth.currentUser) {
        participantId = auth.currentUser.uid;
        authType = 'google';
        email = auth.currentUser.email || undefined;
        displayName = auth.currentUser.displayName || 'Anonymous User';
      } else {
        if (!name) throw new Error('Name is required for anonymous users');
        
        // Initialize or get metadata
        let metadata: ParticipantMetadata;
        const storedData = localStorage.getItem(LOCAL_STORAGE_KEY);
        
        if (storedData) {
          metadata = JSON.parse(storedData);
        } else {
          metadata = {
            localId: uuidv4(),
            pastDecisions: []
          };
        }
        
        participantId = metadata.localId;
        displayName = name;
  
        metadata.pastDecisions = Array.from(new Set([...metadata.pastDecisions, decisionId]));
        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(metadata));
      }
        
      
      const baseParticipant = {
        id: participantId,
        decisionId,
        name: displayName,
        displayName: formatDisplayName(displayName, authType),
        status: 'pending' as ParticipantStatus,
        joinedAt: now,
        lastActive: now,
        createdAt: now,
        auth: {
          type: authType,
          ...(email && { email })
        },
        preferences: {
          notifications: false
        }
      };
      
      const newParticipant = {
        ...baseParticipant,
        ...(auth.currentUser ? { userId: auth.currentUser.uid } : { localId: participantId })
      } as Participant;
  
      // Note: No longer incrementing participants count here
      await runTransaction(db, async (transaction) => {
        const participantRef = doc(db, 'decisions', decisionId, 'participants', participantId);
        transaction.set(participantRef, newParticipant);
      });

      Analytics.trackParticipantJoined(decisionId, authType);
  
      setParticipant(newParticipant);
      return newParticipant;
  
    } catch (err: unknown) {
      console.error('Error joining decision:', err);
      if (err && typeof err === 'object' && 'code' in err && err.code === 'permission-denied') {
        throw new Error('Permission denied. You may need to sign in to join this decision.');
      }
      throw err instanceof Error ? err : new Error('Failed to join decision');
    }
  };

  return {
    participant,
    isLoading,
    error,
    requiresAuth,
    anonymousDisabled,
    joinDecision
  };
}